import { z } from "zod";
import { BotAvatarValueSchema } from "./bot-avatar.js";
import { checkMemoryDocumentPath } from "./bot-bundle.js";
import {
  BOT_DESCRIPTION_MAX_LENGTH,
  BOT_INSTRUCTIONS_MAX_LENGTH,
  BOT_NAME_MAX_LENGTH,
  BOT_TITLE_MAX_LENGTH,
  ScratchpadItemStatusSchema,
  TeachRecordingEventSchema,
  TeachRecordingSchema,
  TeachSnapshotSchema,
  TimeZoneSchema,
} from "./domain.js";
import { McpRemoteEndpointSchema, McpTransportSchema } from "./mcp.js";
import { RoutineWatchSchema } from "./routine-watch.js";
import { SiteSlugSchema } from "./sites.js";

export const SPACE_TRANSFER_LIMITS = {
  maxDataBytes: 64 * 1024 * 1024,
  maxFileBytes: 512 * 1024 * 1024,
  maxTotalBytes: 2 * 1024 * 1024 * 1024,
  maxEntries: 25_000,
  maxCompressedBytes: 2304 * 1024 * 1024,
  maxManifestBytes: 72 * 1024 * 1024,
  maxBots: 200,
  maxRows: 100_000,
  maxDepth: 32,
} as const;

const id = z
  .string()
  .min(1)
  .max(96)
  .regex(/^[A-Za-z0-9_-]+$/);
const date = z.string().datetime();
const short = z.string().max(1000);
const prose = z.string().max(1_000_000);
const nullableId = id.nullable();
const nullableDate = date.nullable();
const row = { id, createdAt: date };
const boundedJson = z.unknown().superRefine((value, ctx) => {
  const pending = [{ value, depth: 0 }];
  let visited = 0;
  while (pending.length) {
    const item = pending.pop()!;
    if (++visited > 100_000 || item.depth > SPACE_TRANSFER_LIMITS.maxDepth) {
      ctx.addIssue({ code: "custom", message: "Structured value is too complex" });
      return;
    }
    const v = item.value;
    if (
      v === null ||
      typeof v === "boolean" ||
      typeof v === "string" ||
      (typeof v === "number" && Number.isFinite(v))
    )
      continue;
    if (Array.isArray(v)) {
      for (const child of v) pending.push({ value: child, depth: item.depth + 1 });
    } else if (v && typeof v === "object" && Object.getPrototypeOf(v) === Object.prototype) {
      for (const [key, child] of Object.entries(v)) {
        if (["__proto__", "prototype", "constructor"].includes(key)) {
          ctx.addIssue({ code: "custom", message: "Unsafe structured key" });
          return;
        }
        pending.push({ value: child, depth: item.depth + 1 });
      }
    } else {
      ctx.addIssue({ code: "custom", message: "Expected JSON data" });
      return;
    }
  }
});
const rows = <T extends z.ZodType>(schema: T, max: number = SPACE_TRANSFER_LIMITS.maxRows) =>
  z.array(schema).max(max);

const PORTABLE_SERVICE_PATHS = new Set([
  "/",
  "/mcp",
  "/sse",
  "/api/mcp",
  "/api/sse",
  "/v1/mcp",
  "/v1/sse",
]);
export const SpaceTransferPortableEndpointSchema = McpRemoteEndpointSchema.refine((value) => {
  const url = new URL(value);
  return (
    !url.search &&
    !value.includes("?") &&
    !value.includes("#") &&
    PORTABLE_SERVICE_PATHS.has(url.pathname)
  );
}, "Only a standard service route without credentials or URL parameters is portable");
export const SpaceTransferRequirementConfigurationSchema = z
  .object({
    transport: McpTransportSchema.optional(),
    endpoint: SpaceTransferPortableEndpointSchema.optional(),
    defaultMemoryScope: z.enum(["isolated", "shared"]).optional(),
    omitted: z
      .array(
        z.enum([
          "endpoint",
          "command",
          "args",
          "env",
          "headers",
          "files",
          "credentials",
          "providerSettings",
          "connectionMetadata",
          "toolAssignments",
          "capabilityConfig",
        ]),
      )
      .max(16),
  })
  .strict();
export const SpaceTransferRequirementSchema = z
  .object({
    kind: z.enum(["secret", "model", "voice", "integration", "memory", "computer", "capability"]),
    name: z.string().min(1).max(200),
    botIds: z.array(id).max(SPACE_TRANSFER_LIMITS.maxBots),
    description: z.string().max(2000).optional(),
    configuration: SpaceTransferRequirementConfigurationSchema.optional(),
  })
  .strict();
export type SpaceTransferRequirement = z.infer<typeof SpaceTransferRequirementSchema>;

export const SpaceTransferTablesSchema = z
  .object({
    bots: rows(
      z
        .object({
          ...row,
          name: z.string().min(1).max(BOT_NAME_MAX_LENGTH),
          title: z.string().max(BOT_TITLE_MAX_LENGTH),
          description: z.string().max(BOT_DESCRIPTION_MAX_LENGTH),
          instructions: z.string().max(BOT_INSTRUCTIONS_MAX_LENGTH),
          selfUpdateInstructions: z.boolean(),
          instructionHistory: boundedJson,
          color: BotAvatarValueSchema,
          notifyOnFinish: z.boolean(),
          pinned: z.boolean(),
          position: z.number().int(),
          sectionId: nullableId,
          archivedAt: nullableDate,
          parentBotId: nullableId,
          memoryScope: short.nullable(),
          computerId: nullableId,
          voiceId: short.nullable(),
          autoSpeak: z.boolean(),
          modelProvider: short.nullable(),
          modelId: short.nullable(),
          ownBackupModels: z.boolean(),
          thinkingLevel: short.nullable(),
          disabledBuiltinTools: z.array(short).max(1000),
          teamChatAmbientEnabled: z.boolean(),
          teamChatRules: prose,
          fleetManagement: z.boolean(),
          // Archives written before coordinators existed carry no such field.
          coordinatorOnly: z.boolean().default(false),
        })
        .strict(),
      SPACE_TRANSFER_LIMITS.maxBots,
    ),
    computers: rows(
      z.object({ id, scope: z.enum(["team", "dedicated"]), kind: z.string().max(80) }).strict(),
      200,
    ),
    sections: rows(z.object({ ...row, name: short, position: z.number().int() }).strict(), 200),
    groups: rows(
      z
        .object({
          ...row,
          name: short,
          pinned: z.boolean(),
          sectionId: nullableId,
          archivedAt: nullableDate,
        })
        .strict(),
      200,
    ),
    groupMembers: rows(z.object({ ...row, groupId: id, botId: id }).strict(), 5000),
    threads: rows(
      z
        .object({
          ...row,
          botId: nullableId,
          groupId: nullableId,
          nextEventSeq: z.number().int().min(0),
          nextMessageSeq: z.number().int().min(0),
          historyCompactedUpToSeq: z.number().int().nullable(),
          historyCompactionSummary: prose.nullable(),
          historyCompactionGeneration: z.number().int().min(0),
        })
        .strict(),
      1000,
    ),
    messages: rows(
      z
        .object({
          ...row,
          threadId: id,
          seq: z.number().int().min(0),
          role: short,
          blocks: boundedJson,
          botId: nullableId,
          replyToMessageId: nullableId,
          replyQuote: prose.nullable(),
          runId: nullableId,
        })
        .strict(),
    ),
    events: rows(
      z
        .object({
          ...row,
          threadId: id,
          botId: id,
          seq: z.number().int().min(0),
          type: short,
          payload: boundedJson,
          runId: nullableId,
        })
        .strict(),
    ),
    tasks: rows(
      z.object({ ...row, botId: id, threadId: id, prompt: prose, status: short }).strict(),
    ),
    runs: rows(
      z
        .object({
          ...row,
          botId: id,
          threadId: id,
          taskId: id,
          status: z.enum(["completed", "failed", "cancelled"]),
          trigger: short,
          modelProvider: short.nullable(),
          modelId: short.nullable(),
          thinkingLevel: short.nullable(),
          error: prose.nullable(),
          sourceMessageId: nullableId,
          routineId: nullableId,
          routineOriginId: nullableId,
          routineName: short.nullable(),
          retryOfRunId: nullableId,
          scheduledFor: nullableDate,
          startedAt: nullableDate,
          completedAt: nullableDate,
        })
        .strict(),
    ),
    routines: rows(
      z
        .object({
          ...row,
          botId: id,
          threadId: nullableId,
          name: short,
          prompt: prose,
          crons: z.array(z.string().max(200)).max(20),
          timezone: TimeZoneSchema,
          notify: z.boolean(),
          webhookEnabled: z.boolean(),
          githubEnabled: z.boolean(),
          messageProvider: short.nullable(),
          modelProvider: short.nullable(),
          modelId: short.nullable(),
          thinkingLevel: short.nullable(),
          watch: RoutineWatchSchema.nullable(),
          scheduledOnceAt: nullableDate,
        })
        .strict(),
      2000,
    ),
    scratchpad: rows(
      z
        .object({
          ...row,
          botId: id,
          title: short,
          status: ScratchpadItemStatusSchema,
          notes: prose,
        })
        .strict(),
      10_000,
    ),
    taughtSkills: rows(
      z
        .object({
          ...row,
          botId: id,
          name: short,
          goal: prose,
          status: z.enum(["draft", "saved"]),
          playbook: boundedJson,
          recording: TeachRecordingSchema.omit({ controlLeaseId: true })
            .extend({
              events: z.array(TeachRecordingEventSchema).max(10_000),
              snapshots: z.array(TeachSnapshotSchema).max(1000),
            })
            .strict(),
        })
        .strict(),
      2000,
    ),
    skills: rows(
      z
        .object({
          ...row,
          name: short,
          description: prose,
          content: prose,
          source: z.enum(["user", "builtin", "plugin"]),
        })
        .strict(),
      2000,
    ),
    memory: rows(
      z
        .object({
          ...row,
          botId: nullableId,
          scope: z.enum(["bot", "user"]),
          path: z
            .string()
            .max(200)
            .refine(checkMemoryDocumentPath, "Expected a relative memory path"),
          content: prose,
          revision: z.number().int().min(1),
        })
        .strict(),
      10_000,
    ),
    memoryRevisions: rows(
      z
        .object({
          ...row,
          documentId: id,
          revision: z.number().int().min(1),
          content: prose,
          sourceRunId: nullableId,
          sourceThreadId: nullableId,
        })
        .strict(),
    ),
    artifacts: rows(
      z
        .object({
          ...row,
          botId: nullableId,
          groupId: nullableId,
          runId: nullableId,
          name: short,
          description: prose.nullable(),
          mimeType: short,
          size: z.number().int().min(0).max(SPACE_TRANSFER_LIMITS.maxFileBytes),
          rootArtifactId: nullableId,
          version: z.number().int().min(1),
        })
        .strict(),
      25_000,
    ),
    botBackupModels: rows(
      z
        .object({ botId: id, provider: short, modelId: short, position: z.number().int().min(0) })
        .strict(),
      2000,
    ),
    spaceBackupModels: rows(
      z.object({ provider: short, modelId: short, position: z.number().int().min(0) }).strict(),
      100,
    ),
    approvalRules: rows(
      z.object({ ...row, effect: short, matchKind: short, matchValue: short }).strict(),
      2000,
    ),
    approvalPreferences: rows(
      z
        .object({ ...row, enabled: z.boolean(), approvalMode: z.enum(["manual", "auto", "none"]) })
        .strict(),
      1,
    ),
    notificationPreferences: rows(
      z.object({ id, finish: z.boolean(), help: z.boolean(), takeover: z.boolean() }).strict(),
      1,
    ),
    boards: rows(
      z
        .object({ ...row, name: short, ticketPrefix: short, nextNumber: z.number().int().min(1) })
        .strict(),
      1,
    ),
    tickets: rows(
      z
        .object({
          ...row,
          boardId: id,
          number: z.number().int().min(1),
          title: short,
          description: prose.nullable(),
          status: short,
          priority: short.nullable(),
          assigneeBotId: nullableId,
          createdByBotId: nullableId,
          byOwner: z.boolean(),
          completedAt: nullableDate,
        })
        .strict(),
      10_000,
    ),
    ticketComments: rows(
      z
        .object({
          ...row,
          ticketId: id,
          authorBotId: nullableId,
          byOwner: z.boolean(),
          body: prose,
        })
        .strict(),
    ),
    sites: rows(
      z
        .object({
          ...row,
          title: z.string().min(1).max(180),
          slug: SiteSlugSchema,
          creatorBotId: nullableId,
          activeReleaseId: nullableId,
        })
        .strict(),
      50,
    ),
    siteAssets: rows(
      z
        .object({
          ...row,
          siteId: id,
          hash: z.string().regex(/^[a-f0-9]{64}$/),
          size: z
            .number()
            .int()
            .min(1)
            .max(50 * 1024 * 1024),
          mimeType: short,
        })
        .strict(),
      6400,
    ),
    siteReleases: rows(
      z
        .object({
          ...row,
          siteId: id,
          version: z.number().int().min(1),
          profile: z.enum(["static", "tools"]),
        })
        .strict(),
      5000,
    ),
  })
  .strict();
export type SpaceTransferTables = z.infer<typeof SpaceTransferTablesSchema>;

export const SpaceTransferDataSchema = z
  .object({
    version: z.literal(1),
    name: z.string().trim().min(1).max(60),
    exportedAt: date,
    tables: SpaceTransferTablesSchema,
    delegations: rows(
      z
        .object({
          requestMessageId: id,
          latestRequestId: id,
          coordinatorId: id,
          workerId: id,
          threadId: id,
          workerRunId: nullableId,
          status: z.enum(["open", "waiting", "complete", "cancelled"]),
          humanSeq: z.number().int().min(0),
          createdAt: date,
          nextCheckAt: date,
        })
        .strict(),
      10000,
    ).optional(),
    requirements: z.array(SpaceTransferRequirementSchema).max(5000),
  })
  .strict();
export type SpaceTransferData = z.infer<typeof SpaceTransferDataSchema>;

export const SpaceTransferCountsSchema = z
  .object({
    bots: z.number().int().min(0),
    groups: z.number().int().min(0),
    messages: z.number().int().min(0),
    skills: z.number().int().min(0),
    memory: z.number().int().min(0),
    routines: z.number().int().min(0),
    sites: z.number().int().min(0),
    files: z.number().int().min(0),
    bytes: z.number().int().min(0),
  })
  .strict();
export type SpaceTransferCounts = z.infer<typeof SpaceTransferCountsSchema>;
export const SpaceTransferImportResultSchema = z
  .object({
    spaceId: id,
    name: z.string().max(60),
    created: z.boolean(),
    counts: SpaceTransferCountsSchema,
    requirements: z.array(SpaceTransferRequirementSchema),
    warnings: z.array(z.string().max(2000)).max(1000),
  })
  .strict();
export type SpaceTransferImportResult = z.infer<typeof SpaceTransferImportResultSchema>;
export const SpaceTransferReviewSchema = z
  .object({
    counts: SpaceTransferCountsSchema,
    requirements: z.array(SpaceTransferRequirementSchema),
    warnings: z.array(z.string().max(2000)).max(1000),
    ready: z.boolean(),
    reason: z.string().max(2000).optional(),
  })
  .strict();
export type SpaceTransferReview = z.infer<typeof SpaceTransferReviewSchema>;
export const SpaceTransferExportResultSchema = z
  .object({
    downloadUrl: z.string(),
    filename: z.string().max(200),
    counts: SpaceTransferCountsSchema,
    bytes: z.number().int().min(0),
  })
  .strict();
export type SpaceTransferExportResult = z.infer<typeof SpaceTransferExportResultSchema>;

export const SpaceTransferExportInputSchema = z
  .object({
    password: z.string().min(12).max(1024),
    reviewed: z.literal(true),
    operationKey: z.string().regex(/^[A-Za-z0-9_-]{16,96}$/),
  })
  .strict();
export type SpaceTransferExportInput = z.infer<typeof SpaceTransferExportInputSchema>;
export const SpaceTransferStoredExportSchema = z
  .object({
    userId: id,
    spaceId: id,
    createdAt: z.number().int().min(0),
    passwordVerifier: z.string().regex(/^[a-f0-9]{64}$/),
    result: SpaceTransferExportResultSchema,
  })
  .strict();
export type SpaceTransferStoredExport = z.infer<typeof SpaceTransferStoredExportSchema>;
